import { Link, Head, usePage } from '@inertiajs/react'
import { ArrowRight, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import CheckoutPreview from '@/components/home/CheckoutPreview'
import VendorListPreview from '@/components/home/VendorListPreview'
import DashboardPreview from '@/components/home/DashboardPreview'
import NonprofitHeroPreview from '@/components/home/NonprofitHeroPreview'
import PlatformSwitchPreview from '@/components/home/PlatformSwitchPreview'
import type { SharedProps } from '@/types'
import type { ReactNode } from 'react'

type Feature = {
  eyebrow: string
  title: string
  body: string
  bullets: string[]
  href: string
  preview: ReactNode
}

const FEATURES: Feature[] = [
  {
    eyebrow: 'Tickets & Donations',
    title: 'Sell tickets and take donations in one night.',
    body: 'Native checkout powered by Stripe Connect — ticket tiers, sponsorship packages, and one-tap donations on the same page. Money lands in your account, not ours.',
    bullets: ['Tiered tickets & tables', 'Tax-deductible receipts, sent automatically', '0% platform fee on donations'],
    href: '/organizations/sign_up',
    preview: <CheckoutPreview />,
  },
  {
    eyebrow: 'Event Services Directory',
    title: 'Find trusted vendors for the night.',
    body: 'A grassroots network of photographers, florists, caterers, and venues — every listing reviewed by our team. Save your favorites and reach out without leaving Gala Season.',
    bullets: ['Reviewed, gala-ready vendors', 'Saved vendor shortlists', 'Reach out with their contact details'],
    href: '/services',
    preview: <VendorListPreview />,
  },
  {
    eyebrow: 'Organizer Dashboard',
    title: 'Run the room with a dashboard built for galas.',
    body: 'Track sales in real time, manage the guest list, check guests in at the door, and post updates to your followers — all before the big night really runs.',
    bullets: ['Live sales & RSVPs', 'Door check-in', 'Posts to your followers'],
    href: '/organizations/sign_up',
    preview: <DashboardPreview />,
  },
]

export default function ForNonprofits() {
  const { app_url } = usePage<SharedProps>().props
  const ogImage = `${app_url}/meta.png`

  return (
    <>
      <Head title="For Nonprofits">
        <meta head-key="description" name="description" content="Everything your gala needs in one platform — native ticketing, donations, a trusted vendor directory, and an organizer dashboard built for charitable events." />
        <meta head-key="og:title" property="og:title" content="For Nonprofits — Gala Season" />
        <meta head-key="og:description" property="og:description" content="One platform, from the first ticket to the last toast. Host extraordinary charitable events with Gala Season." />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={`${app_url}/for-nonprofits`} />
        <meta head-key="og:image" property="og:image" content={ogImage} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content="For Nonprofits — Gala Season" />
        <meta head-key="twitter:description" name="twitter:description" content="One platform, from the first ticket to the last toast." />
        <meta head-key="twitter:image" name="twitter:image" content={ogImage} />
      </Head>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <div className="absolute inset-x-0 top-0 h-[140%] bg-[linear-gradient(to_bottom,oklch(0.85_0.11_62/0.55)_0%,oklch(0.91_0.07_72/0.28)_28%,transparent_62%)]" />
          <div className="absolute -top-1/4 -right-[8%] h-[95%] w-[60%] rounded-full bg-[radial-gradient(circle,oklch(0.80_0.15_55/0.45)_0%,transparent_65%)] blur-2xl" />
        </div>
        <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:py-24">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary">
              <span aria-hidden="true">✦</span>
              For nonprofits
            </p>
            <h1 className="mt-5 text-4xl font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">
              Your list already knows you. Reach{' '}
              <span className="font-serif italic text-primary underline decoration-primary/30 decoration-4 underline-offset-4">
                everyone else&apos;s
              </span>
              .
            </h1>
            <p className="mt-6 max-w-[48ch] text-lg text-foreground text-pretty">
              Get your event in front of donors actively looking for causes. Use our native ticketing — or keep the platform you already have. Either way, donors find you.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button size="lg" asChild>
                <Link href="/organizations/sign_up" className="gap-1.5">
                  Request early access <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/services">Find a vendor first</Link>
              </Button>
            </div>
          </div>
          <div className="min-w-0 lg:pl-8">
            <NonprofitHeroPreview />
          </div>
        </div>
      </section>

      {/* Keep your platform CTA */}
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-black/10 bg-card p-8 shadow-xs sm:p-12">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-primary">Your track, your choice</p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
                Already sell tickets somewhere else? Keep it.
              </h2>
              <p className="mt-5 max-w-[48ch] text-lg text-muted-foreground text-pretty">
                Run ticket sales, auctions, and raffles on whatever platform you already use — Gala Season still puts your event in front of new supporters. Prefer all-in-one? Use our native ticketing for free.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button size="lg" asChild>
                  <Link href="/organizations/sign_up" className="gap-1.5">
                    List your event <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <a href="#how-it-works">See how it works</a>
                </Button>
              </div>
            </div>
            <div className="min-w-0">
              <PlatformSwitchPreview />
            </div>
          </div>
        </div>
      </section>

      {/* Feature rows */}
      <section id="how-it-works" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">Everything the night needs</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-balance sm:text-4xl lg:text-5xl">
            One platform, from the first ticket to the last toast.
          </h2>
        </div>
        <div className="mt-16 space-y-20 lg:space-y-28">
          {FEATURES.map((feature, i) => (
            <div key={feature.eyebrow} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                <p className="text-sm font-semibold uppercase tracking-wide text-primary">{feature.eyebrow}</p>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-balance sm:text-3xl">{feature.title}</h2>
                <p className="mt-4 max-w-[48ch] text-base text-muted-foreground text-pretty">{feature.body}</p>
                <ul className="mt-6 space-y-3">
                  {feature.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-center gap-3 text-base">
                      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <Check className="size-3 text-primary" />
                      </span>
                      {bullet}
                    </li>
                  ))}
                </ul>
                <Button variant="link" asChild className="mt-6 h-auto px-0 text-primary">
                  <Link href={feature.href} className="gap-1">
                    Learn more <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
              <div className={i % 2 === 1 ? 'lg:order-1' : ''}>{feature.preview}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-linear-to-br from-[#F3722B] to-[#F7961E] p-8 text-center sm:p-12">
          <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">Ready to host your own event?</h2>
          <p className="mt-2 text-white/80 font-medium">Create your organization profile and start planning your next charitable event.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link href="/organizations/sign_up" className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-white px-4 text-base font-semibold text-primary transition-all hover:bg-white/90">
              Get Started
            </Link>
            <Button size="lg" variant="outline" className="border-white/30 bg-white/10 font-semibold text-white backdrop-blur-sm hover:bg-white/20 hover:text-white" asChild>
              <Link href="/events">Browse Events</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
